import React, { memo, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Checkbox } from '../../';
import { useTooltip } from '../../../hooks';
import { formatData, formatTooltip, compileTemplateString } from '../../../helpers';

const _DataTableRow = ({
  item,
  sourceHidden,
  depth,
  className,
  hasRows,
  idColumn,
  parentIdColumn,
  primaryColumn,
  columns,
  inlineFieldHasData,
  selected = false,
  editable,
  onSelect,
  actionEdit,
  actionDelete,
  actionRemove,
  actionRun,
  actionAbort,
  onMouseEnterRow,
  onMouseLeaveRow,
  expandable,
  expandableIndentIcons,
  expanded,
  onExpand,
  canExpand,
  childCount = 0,
  viewModes,
  viewMode,
  groupIndex,
}) => {
  const { showTooltip, hideTooltip } = useTooltip();

  const location = useLocation();

  // Resolve the first disabled condition (single object or array) that matches this row's item.
  const runDisabledCondition = (() => {
    if (!actionRun?.disabled) return null;
    const conditions = Array.isArray(actionRun.disabled)
      ? actionRun.disabled
      : [actionRun.disabled];
    return conditions.find(cond => item[cond.field] === cond.value) ?? null;
  })();
  // For a single row, matching any condition is sufficient (ALL === ANY for one item).

  const runIsDisabled = runDisabledCondition !== null;

  /**
   * Reports whether an inline column entry should occupy a slot for this row.
   * First applies the table-wide check (inlineFieldHasData): if no row anywhere has
   * data for this entry, no slot is rendered — keeping rows consistent with the header,
   * which makes the same global `.some()` decision. When some row does have data, a row
   * without its own value still renders an empty spacer so its label stays aligned with
   * sibling rows that render an icon/pill. Pill and text entries are driven by their `value` field;
   * icon entries by their `icon` field.
   * @param {{ type?: string, icon?: string, value?: string }} entry Inline entry definition
   * @returns {boolean} True when the entry should occupy a slot for the current item
   */
  const inlineEntryVisible = entry => {
    // Field the header sorts/keys on: icon entries key on `icon`, pills and text on `value`.
    const field = entry.icon || entry.value;
    // No row in the table has data → collapse the slot everywhere (matches header).
    if (inlineFieldHasData && !inlineFieldHasData(field)) return false;
    return entry.type === 'pill' || entry.type === 'text'
      ? item[entry.value] != null
      : item[entry.icon] !== undefined;
  };

  /**
   * Renders one in-cell inline entry of the primary column: an icon, a pill or a piece of text that
   * belongs beside the row's name rather than in a column of its own.
   *
   * An entry sits before the name by default, or after it with `after: true` — which is how a
   * property that reads as part of the name ("customer_id  int4  NN") is ordered. A text entry can
   * also take `block: true`, which puts it on its own line under the name instead of beside it.
   *
   * @param {object} entry Inline entry definition
   * @param {number} index Position in its own list, for the React key
   * @returns {JSX.Element|null} The entry, or null when this row has nothing to show for it
   */
  const renderInlineEntry = (entry, index) => {
    // Skip entries that have no data to show for this row
    if (!inlineEntryVisible(entry)) return null;

    // Pill entry: e.g. the API method shown before the dataset name
    if (entry.type === 'pill') {
      return (
        <div
          key={index}
          className={
            'primary-column-icon primary-column-pill' +
            (entry.className ? ' ' + entry.className : '')
          }
          onMouseEnter={() =>
            showTooltip({
              ...formatTooltip({
                title: item[entry.label],
                type: 'pill',
                tooltip: item[entry.tooltip],
                value: item[entry.value],
                values: entry.values,
              }),
            })
          }
          onMouseLeave={hideTooltip}
        >
          {formatData(item[entry.label], 'pill', item[entry.value], entry.values)}
        </div>
      );
    }

    // Text entry: a property of this kind of row that the table has no column for, such as a
    // field's datatype in a tree of datasets. Rides in the name cell rather than adding a column
    // that every other kind of row would leave empty.
    if (entry.type === 'text') {
      return (
        <div
          key={index}
          className={
            'primary-column-icon primary-column-text' +
            (entry.className ? ' ' + entry.className : '')
          }
          onMouseEnter={() =>
            showTooltip({
              ...formatTooltip({
                title: item[entry.value],
                tooltip: item[entry.tooltip],
              }),
            })
          }
          onMouseLeave={hideTooltip}
        >
          {item[entry.value]}
        </div>
      );
    }

    // Icon entry (default)
    return (
      <div
        key={index}
        className="primary-column-icon"
        onMouseEnter={() =>
          showTooltip({
            ...formatTooltip({
              title: item[entry.icon],
              type: 'icon',
              tooltip: item[entry.tooltip],
            }),
          })
        }
        onMouseLeave={hideTooltip}
      >
        {formatData(item[entry.icon], 'icon')}
      </div>
    );
  };

  // When abort and run both exist, use the abort button's toolbar `hidden` condition to decide
  // per-row which icon to show — abort is hidden (run shown) when the condition matches this row.
  const abortRowHidden = (() => {
    if (!actionAbort?.hidden) return false;
    const cond = actionAbort.hidden;
    return item[cond.field] === cond.value;
  })();

  const editItem = () => {
    actionEdit([item]);
  };

  const deleteItem = () => {
    actionDelete([item]);
  };

  const removeItem = () => {
    actionRemove([item]);
  };

  const runItem = () => {
    !runIsDisabled && actionRun.onClick && actionRun.onClick([item]);
  };

  const abortItem = () => {
    actionAbort.onClick && actionAbort.onClick([item]);
  };

  const handleMouseEnter = (e, id) => {
    e.preventDefault();
    onMouseEnterRow(e, id);
  };
  const handleMouseLeave = (e, id) => {
    e.preventDefault();
    onMouseLeaveRow(e, id);
  };

  // The item goes along with the id so the table can hand it to a consumer loading children lazily.
  const handleExpand = (e, id) => {
    expandable && onExpand && onExpand(id, item);
  };

  const handleSelect = event => {
    if (onSelect) {
      event.preventDefault();

      onSelect({
        objectName: item.object_name,
        objectId: item[idColumn],
        checked: !selected,
      });
    }
  };

  return useMemo(() => {
    let savedPrimaryColumn;

    return (
      <React.Fragment>
        {
          <tr
            key={item[idColumn] + (item.object_name || '')}
            className={
              'row' +
              (editable && selected ? ' selected' : '') +
              (groupIndex > 1 ? ' grouped' : '') +
              // Rows the source system hides are shown only when the table's "Show hidden" filter
              // is on, and are muted when they are, so they read as present but set apart.
              (sourceHidden ? ' source-hidden' : '') +
              (className ? ` ${className}` : '')
            }
            onMouseEnter={e => onMouseEnterRow && handleMouseEnter(e, item[idColumn])}
            onMouseLeave={e => onMouseLeaveRow && handleMouseLeave(e, item[idColumn])}
          >
            {
              // Render select-row checkbox
              editable && (
                <td className="center checkbox" onClick={handleSelect}>
                  {hasRows && (
                    <Checkbox
                      name={`selected-${item[idColumn]}`}
                      onChange={handleSelect}
                      checked={selected}
                    />
                  )}
                </td>
              )
            }

            {
              // Render columns
              columns.map((column, index) => {
                let isPrimaryColumn = false;
                if (column.id === primaryColumn && !savedPrimaryColumn) {
                  isPrimaryColumn = true;
                  savedPrimaryColumn = column.id;
                }

                const columnLinkTo = column.link && compileTemplateString(column.link, item);

                // Whether this column says anything about this kind of row. A column that does not
                // apply renders an empty cell: a dataset with an unknown row count reads "-", but a
                // field has no row count to be unknown, and saying "-" claims otherwise.
                const applies =
                  !column.appliesTo || [].concat(column.appliesTo).includes(item.object_name);

                return (
                  <React.Fragment key={index}>
                    {
                      // Render chart column type
                      column.type === 'chart' && (
                        <td
                          className={
                            'right' +
                            (column.className === undefined ? '' : ' ' + column.className) +
                            (column.type === 'chart' ? ' chart-label small-text' : '') +
                            (viewModes && column.viewMode && column.viewMode !== viewMode
                              ? ' hide-column'
                              : '')
                          }
                        >
                          {columnLinkTo ? (
                            <Link
                              className="link"
                              to={columnLinkTo}
                              state={{ from: location.pathname }}
                              onMouseEnter={() =>
                                showTooltip({
                                  ...formatTooltip({
                                    title: item[column.id],
                                    type:
                                      column.chartType === 'percent-bar' ? 'percent' : 'integer',
                                    tooltip: item[column.tooltip],
                                    value: item[column.value],
                                    values: column.values,
                                    label: column.label,
                                  }),
                                  subTitle: item[column.tooltipSubtitle],
                                })
                              }
                              onMouseLeave={hideTooltip}
                            >
                              {formatData(
                                item[column.id],
                                column.type,
                                undefined,
                                undefined,
                                undefined,
                                undefined
                              )}
                            </Link>
                          ) : (
                            <React.Fragment>
                              {!applies ? null : item[column.id] == null ? (
                                <span>-</span>
                              ) : (
                                <span
                                  onMouseEnter={() =>
                                    showTooltip({
                                      ...formatTooltip({
                                        title: item[column.id],
                                        type:
                                          column.unit ||
                                          (column.chartType === 'percent-bar'
                                            ? 'percent'
                                            : 'integer'),
                                        tooltip: item[column.tooltip],
                                        value: item[column.value],
                                        values: column.values,
                                        label: column.label,
                                      }),
                                      subTitle: item[column.tooltipSubtitle],
                                    })
                                  }
                                  onMouseLeave={hideTooltip}
                                >
                                  {formatData(
                                    item[column.id],
                                    column.unit ||
                                      (column.chartType === 'percent-bar'
                                        ? 'percent'
                                        : 'integer-format'),
                                    undefined,
                                    undefined,
                                    undefined,
                                    undefined
                                  )}
                                </span>
                              )}
                            </React.Fragment>
                          )}
                        </td>
                      )
                    }

                    <td
                      className={
                        (column.align === undefined ? ' left' : ' ' + column.align) +
                        // A per-row class (":className") resolves to undefined for a row that
                        // carries none, which must not reach the element as the class "undefined".
                        (column.className === undefined
                          ? ''
                          : ' ' + (compileTemplateString(column.className, item) || '')) +
                        (column.type === 'chart' ? ' chart' : '') +
                        (column.type === 'icon' ? ' type-icon' : '') +
                        (column.type === 'button' ? ' type-button' : '') +
                        (column.type === 'boolean' || column.type === 'pill'
                          ? ' type-boolean'
                          : '') +
                        (column.type === 'owner' ? ' type-photo' : '') +
                        (column.type === 'description' ? ' type-description' : '') +
                        (viewModes && column.viewMode && column.viewMode !== viewMode
                          ? ' hide-column'
                          : '') +
                        (isPrimaryColumn ? ' primary-column' : '')
                      }
                    >
                      {(groupIndex > 1 && column.isGrouped) || !applies ? (
                        <React.Fragment></React.Fragment>
                      ) : (
                        <React.Fragment>
                          {columnLinkTo ? (
                            <div
                              className={isPrimaryColumn ? 'primary-column-content' : ''}
                              style={{ paddingLeft: isPrimaryColumn ? depth * 30 + 'px' : '' }}
                            >
                              {
                                // Render expand/collapse control
                                expandable && isPrimaryColumn && (
                                  <div
                                    className="center toggle-expand"
                                    style={
                                      // Collapse the toggle's slot when the first inline entry
                                      // (icon or pill) actually renders for this row.
                                      column.inline &&
                                      column.inline[0] &&
                                      inlineEntryVisible(column.inline[0]) &&
                                      !expandableIndentIcons
                                        ? { width: '0px' }
                                        : {}
                                    }
                                  >
                                    {canExpand ? (
                                      <div
                                        className={
                                          'button-toggle-expand' + (expanded ? ' expanded' : '')
                                        }
                                        onClick={e => handleExpand(e, item[idColumn])}
                                      >
                                        <div className="button-toggle-expand-content"></div>
                                      </div>
                                    ) : (
                                      <div className=""></div>
                                    )}
                                  </div>
                                )
                              }

                              {
                                // In-cell inline entries that belong before the name
                                column.inline &&
                                  column.inline.filter(entry => !entry.after).map(renderInlineEntry)
                              }

                              <div className={isPrimaryColumn ? 'primary-column-label' : ''}>
                                <Link
                                  className="link"
                                  to={columnLinkTo}
                                  state={{ from: location.pathname }}
                                  onMouseEnter={() =>
                                    showTooltip({
                                      ...formatTooltip({
                                        title: item[column.id],
                                        type:
                                          column.type === 'chart'
                                            ? column.chartType === 'percent-bar'
                                              ? 'percent'
                                              : 'integer'
                                            : column.type,
                                        tooltip: item[column.tooltip],
                                        value: item[column.value],
                                        values: column.values,
                                        label: column.label,
                                      }),
                                      subTitle: item[column.tooltipSubtitle],
                                    })
                                  }
                                  onMouseLeave={hideTooltip}
                                >
                                  {formatData(
                                    item[column.id],
                                    column.type,
                                    undefined,
                                    undefined,
                                    undefined,
                                    column.data ? { photo: item[column?.data?.photo] } : undefined
                                  )}
                                </Link>

                                {
                                  // How many children the row has, next to its name. A count the
                                  // table was given wins over the number of rows currently nested
                                  // into it: that count is the whole answer, where the nested rows
                                  // are only the ones directly below — a dataset holding 520 fields
                                  // three levels deep would otherwise read as holding 3. It also
                                  // means the number does not change when the row is opened.
                                  isPrimaryColumn &&
                                    (childCount > 0 ? (
                                      <span className="in-cell-small-text">{childCount}</span>
                                    ) : (
                                      item?.children && (
                                        <span className="in-cell-small-text">
                                          {item.children.length}
                                        </span>
                                      )
                                    ))
                                }

                                {
                                  // An entry marked `block` belongs on its own line under the name,
                                  // so it is rendered inside the name's own element: it then wraps
                                  // and indents with the name, however many lines the name takes.
                                  column.inline &&
                                    column.inline
                                      .filter(entry => entry.after && entry.block)
                                      .map(renderInlineEntry)
                                }
                              </div>

                              {
                                // In-cell inline entries that belong after the name, reading as a
                                // continuation of it rather than as something in front of it
                                column.inline &&
                                  column.inline
                                    .filter(entry => entry.after && !entry.block)
                                    .map(renderInlineEntry)
                              }
                            </div>
                          ) : (
                            <div
                              className={isPrimaryColumn ? 'primary-column-content' : ''}
                              style={{ paddingLeft: isPrimaryColumn ? depth * 30 + 'px' : '' }}
                            >
                              <div className={isPrimaryColumn ? 'primary-column-label' : ''}>
                                {column.email !== undefined ? (
                                  <a
                                    className="link"
                                    href={'mailto:' + item[column.email]}
                                    onMouseEnter={() =>
                                      showTooltip({
                                        ...formatTooltip({
                                          title: item[column.id],
                                          type:
                                            column.type === 'chart'
                                              ? column.chartType === 'percent-bar'
                                                ? 'percent'
                                                : 'integer'
                                              : column.type,
                                          tooltip: item[column.tooltip],
                                          value: item[column.value],
                                          values: column.values,
                                          label: column.label,
                                        }),
                                        subTitle: item[column.tooltipSubtitle],
                                      })
                                    }
                                    onMouseLeave={hideTooltip}
                                  >
                                    {formatData(item[column.id], column.type)}
                                  </a>
                                ) : (
                                  <span
                                    onMouseEnter={() =>
                                      showTooltip({
                                        ...formatTooltip({
                                          title: item[column.id],
                                          type:
                                            column.type === 'chart'
                                              ? column.chartType === 'percent-bar'
                                                ? 'percent'
                                                : 'integer'
                                              : column.type,
                                          tooltip: item[column.tooltip],
                                          value: item[column.value],
                                          values: column.values,
                                          label: column.label,
                                        }),
                                        subTitle: item[column.tooltipSubtitle],
                                      })
                                    }
                                    onMouseLeave={hideTooltip}
                                  >
                                    {formatData(
                                      item[column.id],
                                      column.type,
                                      item[column.value],
                                      column.values,
                                      column.chartType,
                                      column.data
                                        ? {
                                            data: item[column.data.data],
                                            max: item[column.data.max],
                                          }
                                        : undefined,
                                      column.label,
                                      column.onClick
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      )}
                    </td>
                  </React.Fragment>
                );
              })
            }

            {
              // Render edit/delete controls
              editable && (
                <td className="last-column">
                  {(actionEdit || actionDelete || actionRemove || actionRun || actionAbort) && (
                    <div className="buttons">
                      {actionRun && (!actionAbort || abortRowHidden) && (
                        <div
                          name="run"
                          className={'icon-run' + (runIsDisabled ? ' disabled' : '')}
                          onMouseEnter={() =>
                            showTooltip({
                              title: runIsDisabled
                                ? runDisabledCondition.tooltip || 'Already running'
                                : 'Run',
                            })
                          }
                          onMouseLeave={hideTooltip}
                          onClick={() => runItem()}
                        ></div>
                      )}
                      {actionAbort && !abortRowHidden && (
                        <div
                          name="abort"
                          className="icon-remove"
                          onMouseEnter={() => showTooltip({ title: 'Cancel' })}
                          onMouseLeave={hideTooltip}
                          onClick={() => abortItem()}
                        ></div>
                      )}
                      {actionEdit && (
                        <div
                          name="edit"
                          className="icon-edit"
                          onMouseEnter={() => showTooltip({ title: 'Edit' })}
                          onMouseLeave={hideTooltip}
                          onClick={() => editItem()}
                        ></div>
                      )}
                      {actionDelete && (
                        <div
                          name="delete"
                          className="icon-delete"
                          onMouseEnter={() => showTooltip({ title: 'Delete' })}
                          onMouseLeave={hideTooltip}
                          onClick={() => deleteItem()}
                        ></div>
                      )}
                      {actionRemove && (
                        <div
                          name="remove"
                          className="icon-remove"
                          onMouseEnter={() => showTooltip({ title: 'Remove' })}
                          onMouseLeave={hideTooltip}
                          onClick={() => removeItem()}
                        ></div>
                      )}
                    </div>
                  )}
                </td>
              )
            }
          </tr>
        }
      </React.Fragment>
    );
    // Every prop the markup above reads has to be listed here, or the row goes on rendering what it
    // memoised before the prop changed. onSelect is listed because it closes over the current
    // selection: a row holding an older one would drop the rows selected since from the selection
    // it hands back. The action handlers are not — they are recreated on every render of the view
    // that owns them, so listing them would make this memo a no-op.
  }, [
    item,
    sourceHidden,
    selected,
    expanded,
    canExpand,
    childCount,
    depth,
    className,
    editable,
    hasRows,
    groupIndex,
    idColumn,
    primaryColumn,
    onSelect,
    onMouseEnterRow,
    onMouseLeaveRow,
    viewMode,
    columns,
  ]);
};

/**
 * A row of the DataTable.
 *
 * Memoized on its props, so a table where one row opens or is selected renders that row rather than
 * all of them. Every prop it is given must therefore keep its identity across a render that does not
 * concern this row — see the handlers the table holds in refs for the ones that would not.
 */
export const DataTableRow = memo(_DataTableRow);
